import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "../components/Container";
import SectionTitle from "../components/Home/SectionTitle";
import { useSiteContent } from "../context/SiteContentContext";

function QuickLinkCard({ item }) {
  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#8a7355]">
          {item.badge}
        </span>
        <span className="text-sm font-bold text-[#8a7355]">{item.buttonLabel}</span>
      </div>
      <h2 className="mt-5 text-2xl font-bold text-[#3e352b]">{item.title}</h2>
      <p className="mt-3 text-[15px] leading-7 text-[#5d5348]">
        {item.description}
      </p>
    </>
  );

  if (item.type === "route") {
    return (
      <Link
        to={item.target}
        className="block h-full rounded-[28px] border border-[#eadfce] bg-white p-6 text-right shadow-[0_18px_60px_rgba(76,58,37,0.08)] transition-transform hover:-translate-y-1"
      >
        {content}
      </Link>
    );
  }

  return (
    <a
      href={`#${item.target}`}
      className="block h-full rounded-[28px] border border-[#eadfce] bg-white p-6 text-right shadow-[0_18px_60px_rgba(76,58,37,0.08)] transition-transform hover:-translate-y-1"
    >
      {content}
    </a>
  );
}

function SectionShell({ id, badge, title, description, children }) {
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-[32px] border border-[#eadfce] bg-white p-6 shadow-[0_18px_60px_rgba(76,58,37,0.08)] md:p-8"
    >
      <div className="max-w-3xl text-right">
        <p className="text-sm font-bold text-[#8a7355]">{badge}</p>
        <h2 className="mt-3 text-3xl font-bold text-[#3e352b]">{title}</h2>
        <p className="mt-4 text-[16px] leading-7 text-[#5d5348]">{description}</p>
      </div>

      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function Information() {
  const [content, setContent] = useState(null);
  const { tables } = useSiteContent();

  useEffect(() => {
    fetch("/data/information.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`تعذر تحميل صفحة المعلومات: ${response.status}`);
        }

        return response.json();
      })
      .then((json) => setContent(json))
      .catch((error) => console.error(error));
  }, []);

  if (!content) {
    return (
      <main className="pb-20 pt-10">
        <Container>
          <div className="rounded-[28px] border border-[#eadfce] bg-white px-6 py-8 text-right text-[#5d5348]">
            {tables.emptyLabel}
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main className="pb-20">
      <section className="pb-8 pt-10">
        <Container>
          <div className="rounded-[32px] bg-[#f3eee5] px-6 py-10 text-right md:px-10 lg:flex lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-bold text-[#8a7355]">
                {content.hero.eyebrow}
              </p>
              <SectionTitle
                title={content.hero.title}
                classes="mt-3 text-[#3e352b]"
              />
              <p className="mt-4 max-w-2xl text-[16px] leading-7 text-[#5d5348]">
                {content.hero.description}
              </p>
            </div>

            <Link
              to="/"
              className="mt-6 inline-flex rounded-full border border-[#8a7355] px-5 py-3 text-sm font-bold text-[#5d5348] transition-colors hover:bg-[#8a7355] hover:text-white lg:mt-0"
            >
              {tables.backHomeLabel}
            </Link>
          </div>
        </Container>
      </section>

      <section className="pb-8">
        <Container>
          <div className="mb-8 text-right">
            <h2 className="text-2xl font-bold text-[#3e352b]">فهرس المعلومات</h2>
            <p className="mt-3 max-w-3xl text-[16px] leading-7 text-[#5d5348]">
              افتح القسم الذي تريد من البطاقات التالية. صفحة الأعياد تبقى في
              مسارها المستقل، أما بقية مواد  فأعيد تنظيمها هنا
              داخل الصفحة نفسها.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {content.quickLinks.map((item) => (
              <QuickLinkCard key={`${item.type}-${item.target}`} item={item} />
            ))}
          </div>
        </Container>
      </section>

      <section>
        <Container>
          <div className="space-y-8">
            <SectionShell
              id="priests"
              badge={content.priests.badge}
              title={content.priests.title}
              description={content.priests.description}
            >
              <div className="grid gap-6 lg:grid-cols-[0.95fr_1.4fr]">
                <article className="rounded-[28px] bg-[#fcfaf6] p-6 text-right">
                  <h3 className="text-xl font-bold text-[#3e352b]">
                    {content.priests.pioneersTitle}
                  </h3>
                  <p className="mt-3 text-[15px] leading-7 text-[#5d5348]">
                    {content.priests.legacyNote}
                  </p>
                  <div className="mt-5 flex flex-wrap justify-end gap-3">
                    {content.priests.pioneers.map((priest) => (
                      <span
                        key={priest}
                        className="rounded-full bg-white px-4 py-2 text-sm font-bold text-[#6d5d49]"
                      >
                        {priest}
                      </span>
                    ))}
                  </div>
                </article>

                <div className="overflow-x-auto rounded-[28px] border border-[#eadfce]">
                  <table className="min-w-full border-separate border-spacing-0 text-right">
                    <thead>
                      <tr className="bg-[#f8f3eb] text-[#6d5d49]">
                        <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                          الفترة
                        </th>
                        <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                          الاسم
                        </th>
                        <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                          ملاحظات
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-[#4b4034]">
                      {content.priests.entries.map((entry, index) => (
                        <tr
                          key={`${entry.period}-${entry.name}`}
                          className={index % 2 === 0 ? "bg-white" : "bg-[#fcfaf6]"}
                        >
                          <td className="border-b border-[#f0e6d8] px-5 py-4 text-sm">
                            {entry.period}
                          </td>
                          <td className="border-b border-[#f0e6d8] px-5 py-4 text-sm">
                            {entry.name}
                          </td>
                          <td className="border-b border-[#f0e6d8] px-5 py-4 text-sm text-[#6d5d49]">
                            {entry.notes ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </SectionShell>

            <SectionShell
              id="nuns"
              badge={content.nuns.badge}
              title={content.nuns.title}
              description={content.nuns.description}
            >
              <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
                <div className="space-y-6">
                  <article className="rounded-[28px] bg-[#fcfaf6] p-6 text-right">
                    <p className="text-sm font-bold text-[#8a7355]">
                      {content.nuns.orderTitle}
                    </p>
                    <h3 className="mt-2 text-2xl font-bold text-[#3e352b]">
                      {content.nuns.orderName}
                    </h3>
                    <div className="mt-5 flex flex-wrap justify-end gap-3">
                      {content.nuns.names.map((name) => (
                        <span
                          key={name}
                          className="rounded-full bg-white px-4 py-2 text-sm font-bold text-[#6d5d49]"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </article>

                  <article className="rounded-[28px] border border-[#eadfce] bg-white p-6 text-right">
                    <h3 className="text-xl font-bold text-[#3e352b]">
                      {content.nuns.notesTitle}
                    </h3>
                    <div className="mt-4 space-y-4 text-[15px] leading-7 text-[#5d5348]">
                      {content.nuns.notes.map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>
                  </article>
                </div>

                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-1">
                  {content.nuns.images.map((image) => (
                    <figure
                      key={image.src}
                      className="overflow-hidden rounded-[28px] border border-[#eadfce] bg-[#fcfaf6]"
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="h-72 w-full object-cover"
                      />
                      <figcaption className="px-5 py-4 text-right text-sm leading-6 text-[#5d5348]">
                        {image.caption}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </SectionShell>

            <SectionShell
              id="committee"
              badge={content.committee.badge}
              title={content.committee.title}
              description={content.committee.description}
            >
              <div className="grid gap-6 xl:grid-cols-[1fr_1.1fr]">
                <article className="rounded-[28px] bg-[#fcfaf6] p-6 text-right">
                  <div className="space-y-4 text-[15px] leading-7 text-[#5d5348]">
                    {content.committee.intro.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-[#3e352b]">
                    {content.committee.trusteesTitle}
                  </h3>
                  <div className="mt-5 space-y-3">
                    {content.committee.trustees.map((trustee) => (
                      <div
                        key={`${trustee.name}-${trustee.period}`}
                        className="rounded-[22px] border border-[#eadfce] bg-white px-4 py-4"
                      >
                        <p className="text-base font-bold text-[#3e352b]">
                          {trustee.name}
                        </p>
                        <p className="mt-1 text-sm text-[#7b6a54]">
                          {trustee.period}
                        </p>
                      </div>
                    ))}
                  </div>

                  <p className="mt-5 text-sm leading-7 text-[#7b6a54]">
                    {content.committee.trusteesNote}
                  </p>
                </article>

                <article className="rounded-[28px] border border-[#eadfce] bg-white p-6 text-right">
                  <h3 className="text-xl font-bold text-[#3e352b]">
                    {content.committee.membersTitle}
                  </h3>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {content.committee.members.map((member) => (
                      <div
                        key={member}
                        className="rounded-[22px] bg-[#fcfaf6] px-4 py-4 text-sm font-bold text-[#4b4034]"
                      >
                        {member}
                      </div>
                    ))}
                  </div>
                </article>
              </div>
            </SectionShell>

            <SectionShell
              id="patriarchs"
              badge={content.patriarchs.badge}
              title={content.patriarchs.title}
              description={content.patriarchs.description}
            >
              <div className="mb-5 rounded-[24px] bg-[#fcfaf6] px-5 py-4 text-right text-sm leading-7 text-[#6d5d49]">
                {content.patriarchs.note}
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {content.patriarchs.entries.map((entry) => (
                  <article
                    key={`${entry.name}-${entry.period}`}
                    className="rounded-[24px] border border-[#eadfce] bg-[#fffdf9] px-5 py-5 text-right"
                  >
                    <h3 className="text-lg font-bold text-[#3e352b]">
                      {entry.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#7b6a54]">{entry.period}</p>
                  </article>
                ))}
              </div>
            </SectionShell>

            <SectionShell
              id="weekly-program"
              badge={content.weeklyProgram.badge}
              title={content.weeklyProgram.title}
              description={content.weeklyProgram.description}
            >
              <div className="overflow-x-auto rounded-[28px] border border-[#eadfce]">
                <table className="min-w-full border-separate border-spacing-0 text-right">
                  <thead>
                    <tr className="bg-[#f8f3eb] text-[#6d5d49]">
                      <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                        اليوم
                      </th>
                      <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                        البرنامج
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-[#4b4034]">
                    {content.weeklyProgram.entries.map((entry, index) => (
                      <tr
                        key={entry.day}
                        className={index % 2 === 0 ? "bg-white" : "bg-[#fcfaf6]"}
                      >
                        <td className="border-b border-[#f0e6d8] px-5 py-4 text-sm font-bold">
                          {entry.day}
                        </td>
                        <td className="border-b border-[#f0e6d8] px-5 py-4 text-sm leading-7">
                          {entry.schedule}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionShell>
          </div>
        </Container>
      </section>
    </main>
  );
}
